// @ts-strict-ignore
/**
 * @owner @Appboy/clip
 */

import { dismissToast } from "@braze/beacon-core";

import { elevatedAccessToasts } from "./elevatedAccessToasts";

// Memoize returned promise so multiple simultaneous requests don't all open
// separate popup windows.
let pendingRequest: Promise<boolean> | null = null;

let failureToastId: string | number | null = null;

// Opens the elevated access flow in a popup window and returns a promise
// that will be resolved/rejected when the flow is either completed successfully
// or failed.
//
// If the flow is completed, the promise will be resolved with a boolean
// indicating whether access was successfully obtained or not. If the flow was
// abandoned, either due to our time-limit being exceeded or the user closing
// the tab the flow was opened in, we reject the promise with an error.
export const requestElevatedAccess: () => Promise<boolean> = async () => {
  if (pendingRequest) {
    return pendingRequest;
  }

  if (failureToastId != null) {
    dismissToast(failureToastId);
    failureToastId = null;
  }

  const toasts = elevatedAccessToasts.current;

  const width = 800;
  const height = 700;
  const top = window.top.outerHeight / 2 + window.top.screenY - height / 2;
  const left = window.top.outerWidth / 2 + window.top.screenX - width / 2;
  const child = window.open(
    "/elevated-access/entry",
    "_blank",
    `popup,width=${width},height=${height},top=${top},left=${left}`
  );

  if (!child) {
    toasts.popupBlockerDetectedToast();
    return Promise.resolve(false);
  }

  let timeout;
  let childWindowClosedPoller;
  let flowCompletionListener;
  pendingRequest = new Promise<boolean>((resolve, reject) => {
    // Run a callback to check that the child window is still open every
    // 100ms. If the window is closed we reject the promise.
    childWindowClosedPoller = setInterval(() => {
      if (child.closed) {
        failureToastId = toasts.verificationFailureToast();
        reject(new Error("window closed before elevated access flow completed"));
      }
    }, 100);
    // Sets a 5 minute time limit to complete the flow, after which we reject
    // the promise.
    timeout = setTimeout(
      () => {
        child.close();
        failureToastId = toasts.verificationFailureToast();
        reject(new Error("time limit for completing flow exceeded"));
      },
      /* 5 minutes */ 1000 * 60 * 5
    );
    flowCompletionListener = (event: any) => {
      if (event.detail.elevatedAccessObtained) {
        const elevatedAccessObtained = !!event.detail.elevatedAccessObtained;
        if (elevatedAccessObtained) {
          toasts.verificationSuccessToast();
        } else {
          failureToastId = toasts.verificationFailureToast();
        }
        child.close();
        resolve(elevatedAccessObtained);
      }
    };
    window.addEventListener("elevatedAccessEvent", flowCompletionListener);
  }).finally(() => {
    clearInterval(childWindowClosedPoller);
    clearTimeout(timeout);
    window.removeEventListener("elevatedAccessEvent", flowCompletionListener);
    pendingRequest = null;
  });

  return pendingRequest;
};
